import { useState, useEffect, memo, useMemo, useCallback } from 'react'
import { 
  BellIcon, 
  MegaphoneIcon, 
  CalendarIcon, 
  WrenchScrewdriverIcon,
  SparklesIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import { announcementAPI } from '../services/api'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import { useAsyncOperation } from '../hooks/useAsyncOperation'
import { trackAnnouncementInteraction } from '../utils/analytics'
import AnnouncementHeader from './announcements/AnnouncementHeader'
import AnnouncementCard from './announcements/AnnouncementCard'

const AnnouncementsPanel = memo(function AnnouncementsPanel() {
  const { t, i18n } = useTranslation()
  const [expandedId, setExpandedId] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [brokenAvatars, setBrokenAvatars] = useState(new Set())
  const limit = 5

  const { loading, error, data: announcements, execute } = useAsyncOperation()

  // Track announcement toggle
  const handleToggleAnnouncement = useCallback((announcementId, title, willExpand) => {
    setExpandedId(willExpand ? announcementId : null)
    trackAnnouncementInteraction(
      announcementId,
      willExpand ? 'expand' : 'collapse',
      title
    )
  }, [])

  // Memoize announcement splits BEFORE any conditional returns
  const pinnedAnnouncements = useMemo(() => announcements?.filter(a => a.isPinned) || [], [announcements])
  const regularAnnouncements = useMemo(() => announcements?.filter(a => !a.isPinned) || [], [announcements])

  // Memoize helper functions to prevent unnecessary re-renders of child components
  const getTypeIcon = useCallback((type) => {
    switch (type) {
      case 'update':
        return BellIcon
      case 'event':
        return CalendarIcon
      case 'maintenance':
        return WrenchScrewdriverIcon
      case 'feature':
        return SparklesIcon
      default:
        return InformationCircleIcon
    }
  }, [])

  const getTypeColor = useCallback((type) => {
    switch (type) {
      case 'update':
        return 'bg-blue-50 text-blue-700 border-blue-200'
      case 'event':
        return 'bg-purple-50 text-purple-700 border-purple-200'
      case 'maintenance':
        return 'bg-gray-50 text-gray-700 border-gray-200'
      case 'feature':
        return 'bg-green-50 text-green-700 border-green-200'
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200'
    }
  }, [])

  const getPriorityIndicator = useCallback((priority) => {
    if (priority === 'urgent' || priority === 'high') {
      return (
        <div className="flex-shrink-0 w-1 h-1 rounded-full bg-primary" title={priority}></div>
      )
    }
    return null
  }, [])

  const formatDate = useCallback((dateString) => {
    if (!dateString) return ''
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true })
    } catch {
      return ''
    }
  }, [])

  const generateShareLink = useCallback((announcement) => {
    const slug = announcement.title.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
    const baseUrl = window.location.origin
    return `${baseUrl}/?announcement=${slug}&lang=${i18n.language}`
  }, [i18n.language])

  const toggleExpand = useCallback((id, title) => {
    const willExpand = expandedId !== id
    handleToggleAnnouncement(id, title, willExpand)
  }, [expandedId, handleToggleAnnouncement])

  const fetchAnnouncements = useCallback(async () => {
    await execute(
      async () => {
        // Pass current language to get announcements in user's preferred language
        const response = await announcementAPI.getPublishedAnnouncements({ 
          page, 
          limit, 
          language: i18n.language 
        })
        setTotalPages(response.data.totalPages || 1)
        setTotal(response.data.total || 0)
        return response.data.data || []
      },
      { silent: true } // Don't show error toast, we'll handle it in the UI
    )
  }, [page, i18n.language, execute])

  useEffect(() => {
    fetchAnnouncements()
  }, [fetchAnnouncements])

  // Auto-expand: first pinned announcement, or first overall if none are pinned
  useEffect(() => {
    if (!announcements || announcements.length === 0) return
    if (expandedId !== null) return // don't override user toggle or deep-link
    const first = pinnedAnnouncements[0] || announcements[0]
    if (first) setExpandedId(first.id)
  }, [announcements, pinnedAnnouncements]) // eslint-disable-line react-hooks/exhaustive-deps

  // Deep linking: Auto-expand announcement from URL parameter and switch language
  // Note: Parent component (HomePageMap) handles tab switching
  useEffect(() => {
    if (!announcements || announcements.length === 0) return

    const params = new URLSearchParams(window.location.search)
    const announcementId = params.get('id')
    const announcementSlug = params.get('announcement')
    const targetLang = params.get('lang')

    // Switch language if specified in URL and different from current
    if (targetLang && targetLang !== i18n.language) {
      i18n.changeLanguage(targetLang)
    }

    // Support both id-based (from sitemap) and slug-based deep linking
    let announcement = null
    
    if (announcementId) {
      // Direct ID match (from sitemap)
      announcement = announcements.find(a => a.id === announcementId)
    } else if (announcementSlug) {
      // Slug-based match (legacy)
      announcement = announcements.find(a => {
        const slug = a.title.toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim()
        return slug === announcementSlug
      })
    }

    if (announcement) {
      // Auto-expand the announcement
      setExpandedId(announcement.id)
      
      // Track deep link navigation
      trackAnnouncementInteraction(
        announcement.id,
        'deep_link',
        announcement.title
      )

      // Scroll to announcement after a brief delay to ensure rendering
      setTimeout(() => {
        const element = document.getElementById(`announcement-${announcement.id}`)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 500)
    }
  }, [announcements, i18n])

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200/50 p-3 animate-pulse">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
              <div className="flex-1 min-w-0">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <MegaphoneIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-500 text-sm">{error}</p>
      </div>
    )
  }

  if (!announcements || announcements.length === 0) {
    return (
      <div className="text-center py-12">
        <MegaphoneIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium mb-1">
          {t('announcements.empty', 'No announcements at this time')}
        </p>
        <p className="text-gray-400 text-xs">
          Check back later for updates
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3 px-1">
      {/* Header with pagination and count */}
      <AnnouncementHeader
        t={t}
        total={total}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      {/* Pinned Announcements */}
      {pinnedAnnouncements.length > 0 && (
        <>
          {pinnedAnnouncements.map((announcement) => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              isExpanded={expandedId === announcement.id}
              onToggle={toggleExpand}
              onGenerateShareLink={generateShareLink}
              getTypeIcon={getTypeIcon}
              getTypeColor={getTypeColor}
              getPriorityIndicator={getPriorityIndicator}
              formatDate={formatDate}
              t={t}
              isPinned={true}
              brokenAvatars={brokenAvatars}
              setBrokenAvatars={setBrokenAvatars}
            />
          ))}
        </>
      )}

      {/* Regular Announcements */}
      {regularAnnouncements.map((announcement) => (
        <AnnouncementCard
          key={announcement.id}
          announcement={announcement}
          isExpanded={expandedId === announcement.id}
          onToggle={toggleExpand}
          onGenerateShareLink={generateShareLink}
          getTypeIcon={getTypeIcon}
          getTypeColor={getTypeColor}
          getPriorityIndicator={getPriorityIndicator}
          formatDate={formatDate}
          t={t}
          isPinned={false}
          brokenAvatars={brokenAvatars}
          setBrokenAvatars={setBrokenAvatars}
        />
      ))}
    </div>
  )
})

export default AnnouncementsPanel
